import { useEffect, useRef, useState } from "react";
import BrandShell from "../components/BrandShell";
import SEO from "../components/SEO";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Mail, Upload, Send, Eye, Trash2, UserPlus } from "lucide-react";

const STATUS = (r) => r.unsubscribed ? ["Unsubscribed", "text-zinc-500"] : r.joined ? ["Joined ✓", "text-emerald-300"]
  : r.reminder_at ? ["Reminded", "text-sky-300"] : r.invite_status === "failed" ? ["Failed", "text-red-300"] : r.invite_at ? ["Invited", "text-amber-300"] : ["Pending", "text-zinc-300"];

function Stat({ label, value, testid }) {
  return <div className="glass rounded-xl p-3" data-testid={testid}><div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div><div className="text-xl font-bold text-white">{value}</div></div>;
}

export default function AdminLeads() {
  const [data, setData] = useState({ rows: [], counts: {} });
  const [runs, setRuns] = useState([]);
  const [testEmails, setTestEmails] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const load = () => Promise.all([api.get("/admin/leads"), api.get("/admin/leads/runs")]).then(([a, b]) => { setData(a.data); setRuns(b.data.rows); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const importCsv = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try {
      const { data: r } = await api.post("/admin/leads/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${r.added} new leads added (${r.already_in_list} already in list, ${r.already_registered} already on Nexus)`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Import failed"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const sendTest = async (reminder = false) => {
    const emails = testEmails.split(/,|\n/).map((x) => x.trim()).filter(Boolean);
    if (!emails.length) return toast.error("Enter at least one email");
    setBusy(true);
    try {
      const { data: r } = await api.post("/admin/leads/test", { emails, reminder });
      const ok = r.results.filter((x) => x.sent).length;
      ok === emails.length ? toast.success(`Test ${reminder ? "reminder" : "invite"} sent to ${ok}`) : toast.error(`Sent ${ok}/${emails.length} — ${r.results.find((x) => !x.sent)?.error || "check Resend"}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Test failed"); }
    finally { setBusy(false); }
  };

  const showPreview = async (reminder) => {
    const { data: r } = await api.get("/admin/leads/preview", { params: { reminder } });
    setPreview(r);
  };

  const sendAll = async () => {
    const n = data.counts.pending || 0;
    if (!window.confirm(`Send the invite to all ${n} pending leads now? This emails real people.`)) return;
    setBusy(true);
    try {
      const { data: r } = await api.post("/admin/leads/send", {});
      toast.success(`Sending to ${r.queued} leads — reminders go out automatically after 5 days`);
      setTimeout(load, 2000); setTimeout(load, 15000);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Send failed"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => { await api.delete(`/admin/leads/${id}`); load(); };
  const c = data.counts;

  return (
    <BrandShell>
      <SEO title="Invite leads · Admin" description="Invite creator leads to Nexus." path="/admin/leads" />
      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-10 py-8 space-y-6" data-testid="admin-leads-page">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2 flex items-center gap-2"><UserPlus className="h-3.5 w-3.5 text-nexus-pink" /> Admin</div>
          <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tighter">Invite creator leads</h1>
          <p className="text-zinc-400 mt-2 text-sm">Upload a CSV of creators (Creator Pack form export works as-is). Each gets a personalised invite from Nexus; anyone who hasn't joined after 5 days gets one automatic reminder. Leads who sign up or unsubscribe are tracked here.</p>
        </div>

        <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
          <Stat label="Total" value={c.total ?? "…"} testid="leads-stat-total" /><Stat label="Pending" value={c.pending ?? "…"} testid="leads-stat-pending" />
          <Stat label="Invited" value={c.invited ?? "…"} testid="leads-stat-invited" /><Stat label="Reminded" value={c.reminded ?? "…"} testid="leads-stat-reminded" />
          <Stat label="Joined" value={c.joined ?? "…"} testid="leads-stat-joined" /><Stat label="Unsubscribed" value={c.unsubscribed ?? "…"} testid="leads-stat-unsub" />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="glass rounded-2xl p-5 space-y-3">
            <div className="text-xs uppercase tracking-widest text-zinc-500 flex items-center gap-2"><Upload className="h-3.5 w-3.5" /> 1 · Import CSV</div>
            <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={(e) => importCsv(e.target.files?.[0])} className="block w-full text-sm text-zinc-300 file:mr-3 file:rounded-full file:border-0 file:bg-white/10 file:px-4 file:py-2 file:text-white file:text-xs" data-testid="leads-csv-input" />
            <p className="text-xs text-zinc-500">Needs an email column. Name, niche, location and handles are picked up automatically when present. Duplicates and people already on Nexus are skipped.</p>
          </div>
          <div className="glass rounded-2xl p-5 space-y-3">
            <div className="text-xs uppercase tracking-widest text-zinc-500 flex items-center gap-2"><Mail className="h-3.5 w-3.5" /> 2 · Send yourself a test</div>
            <p className="text-xs text-zinc-500">Use <span className="text-zinc-300">Name &lt;email&gt;</span> so each test is personalised. Real sends always use each lead's own name and niche from the CSV.</p>
            <Input value={testEmails} onChange={(e) => setTestEmails(e.target.value)} placeholder="Chaz <you@email.com>, Angelo <partner@email.com>" className="bg-white/[0.02] border-white/10 text-white" data-testid="leads-test-emails" />
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" onClick={() => sendTest(false)} disabled={busy} className="rounded-full nexus-gradient text-white border-0" data-testid="leads-test-send-btn"><Send className="h-3.5 w-3.5 mr-1.5" /> Send test invite</Button>
              <Button size="sm" variant="outline" onClick={() => sendTest(true)} disabled={busy} className="rounded-full border-white/15 text-zinc-200" data-testid="leads-test-reminder-btn">Send test reminder</Button>
              <Button size="sm" variant="ghost" onClick={() => showPreview(false)} className="rounded-full text-zinc-300" data-testid="leads-preview-btn"><Eye className="h-3.5 w-3.5 mr-1.5" /> Preview</Button>
            </div>
          </div>
        </div>

        <div className="glass rounded-2xl p-5 flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-500">3 · Send to everyone pending</div>
            <p className="text-sm text-zinc-400 mt-1">Only leads who haven't been invited, joined or unsubscribed. Sent one at a time to stay within email limits.</p>
          </div>
          <Button onClick={sendAll} disabled={busy || !c.pending} className="rounded-full nexus-gradient text-white border-0 font-semibold" data-testid="leads-send-all-btn"><Send className="h-4 w-4 mr-2" /> Send invite to {c.pending ?? "…"} leads</Button>
        </div>

        {runs.length > 0 && (
          <div className="text-xs text-zinc-400 space-y-1" data-testid="leads-runs">
            {runs.map((r) => <div key={r.id}>{new Date(r.created_at).toLocaleString("en-GB")} · {r.status === "done" ? `Sent ${r.sent}/${r.total}` : `Sending… ${r.total}`} · by {r.by}</div>)}
          </div>
        )}

        <div className="glass rounded-2xl overflow-hidden">
          <table className="w-full text-sm" data-testid="leads-table">
            <thead className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/5"><tr><th className="text-left p-3">Creator</th><th className="text-left p-3 hidden md:table-cell">Niche</th><th className="text-left p-3 hidden lg:table-cell">Location</th><th className="text-left p-3">Status</th><th className="p-3" /></tr></thead>
            <tbody>
              {data.rows.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-zinc-500" data-testid="leads-empty">No leads yet — import a CSV above.</td></tr>}
              {data.rows.map((r) => { const [label, cls] = STATUS(r); return (
                <tr key={r.id} className="border-b border-white/5" data-testid={`lead-row-${r.id}`}>
                  <td className="p-3"><div className="text-white">{r.name || "—"}</div><div className="text-xs text-zinc-500">{r.email}</div></td>
                  <td className="p-3 text-zinc-400 hidden md:table-cell max-w-[260px] truncate">{r.niche || "—"}</td>
                  <td className="p-3 text-zinc-400 hidden lg:table-cell">{r.location || "—"}</td>
                  <td className={`p-3 text-xs ${cls}`}>{label}{r.invite_at && <div className="text-zinc-600">{new Date(r.invite_at).toLocaleDateString("en-GB")}</div>}</td>
                  <td className="p-3 text-right"><button onClick={() => remove(r.id)} className="text-zinc-600 hover:text-red-300" aria-label="Remove lead" data-testid={`lead-delete-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></button></td>
                </tr>); })}
            </tbody>
          </table>
        </div>

        {preview && (
          <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setPreview(null)} data-testid="leads-preview-modal">
            <div className="bg-[#0A0A0F] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-auto border border-white/10" onClick={(e) => e.stopPropagation()}>
              <div className="p-3 border-b border-white/10 text-sm text-zinc-300">Subject: <span className="text-white">{preview.subject}</span></div>
              <iframe title="Email preview" srcDoc={preview.html} className="w-full h-[70vh] bg-[#0A0A0F]" />
            </div>
          </div>
        )}
      </div>
    </BrandShell>
  );
}
